// Electronic Press Kit (EPK) source of truth.
//
// Every value here is repository-backed and first-party. No media coverage,
// journalist names, quotes, awards, or external profiles are invented — the
// only outbound URLs are the verified streaming/profile links in content.ts.
//
// Consumed by:
//   /press               — the human-readable EPK
//   /press/kit.json      — the machine-readable EPK
//   /press/releases      — press-release archive
//   /press/kit/$slug     — release-specific press kits

import buddhaKaPortrait from "@/assets/buddha-ka-official.png.asset.json";
import { BOOKS, STREAMING_LINKS } from "@/lib/content";
import { SITE_URL, absoluteUrl } from "@/lib/site";
import { CANONICAL_SHORT_DESCRIPTION, factValue } from "@/lib/entity";

export const PRESS_CONTACT_PATH = "/contact";

export const BIO_50 =
  "Buddha KA (Curtis Shores) is a Chicago-born independent recording artist, author, and founder of KAfrequency — a first-party ecosystem uniting music, books, daily activations, visual worlds, and consciousness systems.";

export const BIO_150 =
  "Buddha KA — legal name Curtis Shores — is a Chicago-born independent recording artist, author, and creator of KAfrequency, the artist-owned ecosystem he founded to unite music, written systems, daily spoken activations, visual worlds, and consciousness technologies under one canon. His musical framework, KA Flow, integrates hip-hop, soul and R&B, spoken wisdom, and spiritual science into a cinematic language written to be replayed for years rather than consumed in a scroll. Out now: The Diamond Sutra, released August 19, 2026.";

export const BIO_400: string[] = [
  "Buddha KA — legal name Curtis Shores — is a Chicago-born independent recording artist, author, visual world-builder, and founder of KAfrequency, a first-party creative ecosystem uniting music, books, daily spoken activations, cinematic imagery, and consciousness systems under one canon.",
  "He was born and raised in Chicago until age 13, then moved to Wisconsin, and to Las Vegas at 16. In the years since, he has continued to live and work between Chicago, Texas, and Las Vegas. The geography sits inside the work: Chicago's rhythmic literacy, the interior stillness of the Wisconsin years, and the desert clarity of the Southwest each pressed into how his music sounds and how his writing thinks.",
  "His musical framework, KA Flow, integrates hip-hop, soul and R&B, spoken wisdom, and spiritual science into one coherent musical language. KA Flow governs the KAfrequency catalog — including the sutra cycle, Golden Frequency, The Golden Wave, and Ascended and Armed — and is the throughline of The Diamond Sutra, released August 19, 2026.",
  "His written work includes 47 Gates of Consciousness, The Crystallized Mind, and companion working manuals — books built as practice systems rather than commentary. The daily activations extend the same framework into a short, spoken practice for ordinary life. The visual arm builds Kemetic-rooted imagery, gold and obsidian tonality, and portraits engineered as symbols. The consciousness-systems arm is the interactive work: the 47 Gates journey, the Life Journey Map, the Academy paths, and the AI-supported study surface.",
  "Buddha KA operates the entire ecosystem himself, outside the traditional label system, and treats music as inner technology rather than entertainment.",
];

/** One-paragraph organisation boilerplate appended to every press release. */
export const BOILERPLATE =
  "About KAfrequency — KAfrequency is the artist-owned creative ecosystem founded and architected by Buddha KA (Curtis Shores). It unites music, books, daily spoken activations, visual worlds, and consciousness systems under one canon, published first-party at buddhaka.com.";

/** Canonical one-line identity, governed by the entity registry. */
export const CANONICAL_IDENTITY_LINE = CANONICAL_SHORT_DESCRIPTION;

export const FACT_SHEET: Array<[string, string]> = [
  ["Professional name", "Buddha KA"],
  ["Legal / author byline", "Curtis Shores"],
  ["Origin", "Chicago, Illinois (born)"],
  ["Journey", "Chicago → Wisconsin (age 13) → Las Vegas (age 16); continues between Chicago, Texas, and Las Vegas"],
  ["Roles", (factValue("person.roles").display as string[]).join(", ")],
  ["Founder of", "KAfrequency"],
  ["Musical framework", "KA Flow — hip-hop, soul/R&B, spoken wisdom, spiritual science"],
  ["Ecosystem", "Music · Books · Daily spoken activations · Visual worlds · Consciousness systems"],
  ["Latest release", "The Diamond Sutra — released August 19, 2026"],
  ["Official website", "https://buddhaka.com"],
];

export const ANGLES: string[] = [
  "The rise of first-party, artist-owned creative ecosystems in music.",
  "Spiritual science inside modern hip-hop: how KA Flow was built.",
  "Music as inner technology — the case for records engineered as instruction.",
  "From Chicago to Wisconsin to Las Vegas: what the geography did to the sound.",
  "Linking music, books, daily activations, and visual worlds into one canon.",
  "Kemetic imagery and the discipline of visual identity in an algorithmic era.",
];

export const USAGE_TERMS: string[] = [
  'Editorial outlets may use the supplied official artwork when covering Buddha KA or KAfrequency, crediting "Courtesy of Buddha KA / KAfrequency".',
  "Do not crop, recolour, overlay, or otherwise alter the official portrait or cover artwork.",
  "Advertising, product packaging, merchandise, and derivative works require written permission.",
  "Biographies and the boilerplate may be reproduced verbatim; please do not paraphrase biographical facts.",
];


export interface PressAsset {
  id: string;
  label: string;
  /** Absolute URL to the original, downloadable file. */
  url: string;
  kind: "portrait" | "cover";
  credit: string;
  alt: string;
}

/** Downloadable press assets — only files that exist in this repository. */
export const PRESS_ASSETS: PressAsset[] = [
  {
    id: "official-portrait",
    label: "Buddha KA — official portrait",
    url: absoluteUrl(buddhaKaPortrait.url),
    kind: "portrait",
    credit: "Courtesy of Buddha KA / KAfrequency",
    alt: "Buddha KA — official press portrait",
  },
  ...BOOKS.filter((b) => Boolean(b.coverUrl)).map((b) => ({
    id: `book-${b.slug}`,
    label: `${b.title} — cover artwork`,
    url: absoluteUrl(b.coverUrl!),
    kind: "cover" as const,
    credit: "Courtesy of Buddha KA / KAfrequency",
    alt: `${b.title} — cover artwork`,
  })),
];

/** Verified official destinations. Never a fabricated or unverified profile. */
export const OFFICIAL_PROFILES = Object.entries(STREAMING_LINKS)
  .filter((entry): entry is [string, string] => typeof entry[1] === "string" && entry[1].length > 0)
  .map(([platform, url]) => ({ platform, url }));

/** Every machine-readable press resource this site publishes. */
export const PRESS_RESOURCES: Array<{
  id: string;
  label: string;
  path: string;
  type: string;
  description: string;
}> = [
  {
    id: "press-kit-json",
    label: "Electronic Press Kit (JSON)",
    path: "/press/kit.json",
    type: "application/json",
    description: "Bios, fact sheet, assets, discography, and press releases in one machine-readable document.",
  },
  {
    id: "newsroom-rss",
    label: "Newsroom RSS 2.0",
    path: "/feed.xml",
    type: "application/rss+xml",
    description: "Every official newsroom entry, newest first.",
  },
  {
    id: "newsroom-atom",
    label: "Newsroom Atom 1.0",
    path: "/atom.xml",
    type: "application/atom+xml",
    description: "The same newsroom stream in Atom 1.0 for readers that prefer it.",
  },
  {
    id: "press-releases-rss",
    label: "Press releases RSS 2.0",
    path: "/press/releases.xml",
    type: "application/rss+xml",
    description: "Formal announcements only, excluding features and profiles.",
  },
  {
    id: "news-sitemap",
    label: "Google News sitemap",
    path: "/news-sitemap.xml",
    type: "application/xml",
    description: "Rolling 30-day news sitemap with publication annotations.",
  },
  {
    id: "discography-jsonld",
    label: "Discography JSON-LD",
    path: "/music/catalog.json",
    type: "application/ld+json",
    description: "The full KAfrequency catalog as a structured entity graph.",
  },
];

export const PRESS_FEED_TITLE = "Buddha KA · KAfrequency — Official Newsroom";
export const PRESS_FEED_DESCRIPTION =
  "Official news, release announcements, creative frameworks, and project updates from Buddha KA and KAfrequency.";
export const PRESS_RELEASE_FEED_TITLE = "Buddha KA · KAfrequency — Press Releases";
export const PRESS_RELEASE_FEED_DESCRIPTION =
  "Formal first-party press releases and announcements from Buddha KA and KAfrequency.";
export const FEED_COPYRIGHT = `© ${new Date().getUTCFullYear()} KAfrequency. All rights reserved.`;
export const PUBLICATION_NAME = "Buddha KA · KAfrequency Official Newsroom";

export const PRESS_KIT_URL = `${SITE_URL}/press`;

/* ------------------------------------------------------------------ *
 * Approved press-kit copy (owner-authored, verbatim)
 * ------------------------------------------------------------------ */

/** ~30 words. Short listing / directory blurb. */
export const PRESS_BIO_30 =
  "Buddha KA is an independent artist, author, and creator of KAfrequency, blending conscious hip-hop, spiritual science, Kemetic thought, Buddhist philosophy, and disciplined self-mastery into music built for practice and transformation.";

/** ~100 words. Standard press paragraph. */
export const PRESS_BIO_100 =
  "Buddha KA — legal name Curtis Shores — is a Chicago-born independent recording artist, author, and creator of KAfrequency, the artist-owned ecosystem uniting music, books, daily spoken activations, visual worlds, and consciousness systems under one canon. His musical framework, KA Flow, integrates hip-hop, soul and R&B, spoken wisdom, and spiritual science into a cinematic language written to be studied and replayed rather than scrolled past. Kemetic thought, Buddhist philosophy, and disciplined self-mastery run through the catalog as method, not decoration. He operates the entire ecosystem himself, outside the traditional label system. Out now: The Diamond Sutra, released August 19, 2026.";

/** Long-form bio, paragraph by paragraph. */
export const PRESS_BIO_FULL: string[] = BIO_400;

/** Official statement for the announced album. */
export const DIAMOND_SUTRA_STATEMENT: string[] = [
  "The Diamond Sutra is the latest KAfrequency album, released August 19, 2026 across seventeen tracks.",
  "The record borrows the sutra's central instrument — the cutting of illusion — and applies it to modern attention: borrowed eyes, borrowed minds, and the quiet ways a life gets designed by someone else. It is not presented as an adaptation of scripture or a claim of scholarship. The sutra is the intellectual frame; the writing is first-person, contemporary, and built for practice.",
  "Every track has its own written authority page on buddhaka.com, so the album can be read as well as heard.",
];

/** Direct press contact. Published deliberately for journalists. */
export const PRESS_EMAIL = factValue("press.email");

/** Verified first-party and official destinations, for the press links block. */
export const PRESS_OFFICIAL_LINKS: Array<{ label: string; url: string; note?: string }> = [
  { label: "Official website", url: SITE_URL, note: "Canonical source for all Buddha KA / KAfrequency information" },
  { label: "Artist identity page", url: `${SITE_URL}/artist/buddha-ka` },
  { label: "Verified fact sheet", url: `${SITE_URL}/press/factsheet`, note: "Citation-ready facts, bios, and official links" },
  { label: "The Diamond Sutra press kit", url: `${SITE_URL}/press/the-diamond-sutra` },
  { label: "Discography", url: `${SITE_URL}/music/discography` },
  ...OFFICIAL_PROFILES.map((p) => ({ label: p.platform, url: p.url })),
];

